import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import {
  PbWithdrawalRequestStatusEnum as Status,
  type PbWithdrawalRequest
} from '@itsrever/rever-api'

const { Badge, createColumnHelper } = Base
const columnHelper = createColumnHelper<PbWithdrawalRequest>()

type Tone = 'neutral' | 'info' | 'success' | 'warning'

// Returned is amber: the goods are back and the refund is the merchant's to make.
export const STATUSES: Record<string, { key: string; tone: Tone }> = {
  [Status.Received]: { key: 'withdrawals.status_received', tone: 'neutral' },
  [Status.AwaitingReturn]: {
    key: 'withdrawals.status_awaiting_return',
    tone: 'info'
  },
  [Status.Returned]: { key: 'withdrawals.status_returned', tone: 'warning' },
  [Status.Refunded]: { key: 'withdrawals.status_refunded', tone: 'success' },
  [Status.Closed]: { key: 'withdrawals.status_closed', tone: 'neutral' }
}

const DONE: string[] = [Status.Refunded, Status.Closed]

export type WithdrawalClosing = {
  // Only write-all users close requests.
  canClose: boolean
  pendingId?: string
  toggle: (w: PbWithdrawalRequest) => void
}

const NOWRAP = {
  cellClassName: 'whitespace-nowrap',
  headClassName: 'whitespace-nowrap'
}

// shopifyStore, the selected store's, links each of its matched orders to its page in the Shopify admin.
export function useWithdrawalColumns(
  closing: WithdrawalClosing,
  ecommerceId: string | null | undefined,
  shopifyStore?: string
) {
  const { t, i18n } = useTranslation()
  const { canClose, pendingId, toggle } = closing
  return useMemo(() => {
    const languages = new Intl.DisplayNames([i18n.language], {
      type: 'language'
    })
    const date = (value: string) =>
      new Date(value).toLocaleDateString(i18n.language, { dateStyle: 'medium' })
    return [
      columnHelper.accessor('received_at', {
        header: t('withdrawals.received'),
        cell: (info) => {
          const at = new Date(info.getValue() ?? '')
          return (
            <div className="flex flex-col">
              <span className="text-sm text-neutral-900">
                {at.toLocaleDateString(i18n.language, { dateStyle: 'medium' })}
              </span>
              <span className="text-xs tabular-nums text-neutral-600">
                {at.toLocaleTimeString(i18n.language, { timeStyle: 'medium' })}
              </span>
            </div>
          )
        },
        meta: NOWRAP
      }),
      columnHelper.accessor('name', {
        header: t('withdrawals.customer'),
        cell: (info) => (
          <div className="flex flex-col">
            <span className="text-sm text-neutral-900">{info.getValue()}</span>
            <span className="text-xs text-neutral-600">
              {info.row.original.email}
            </span>
          </div>
        )
      }),
      columnHelper.accessor('order_number', {
        header: t('withdrawals.order'),
        cell: (info) => {
          const w = info.row.original
          const ends = w.withdrawal_period_ends_at
          const late = !!ends && !!w.received_at && w.received_at > ends
          return (
            <div className="flex flex-col">
              {/* A previous store's rows linger while the new store's load. */}
              {shopifyStore && w.order_id && w.ecommerce_id === ecommerceId ? (
                // Same URL ecommerce-api builds for the return's order. Its own tab, so the row isn't opened.
                <a
                  href={`https://${shopifyStore}.myshopify.com/admin/orders/${w.order_id}`}
                  target="_blank"
                  rel="noreferrer"
                  title={t('withdrawals.open_in_shopify')}
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm text-primary-base hover:underline"
                >
                  {info.getValue()}
                </a>
              ) : (
                <span className="text-sm text-neutral-900">
                  {info.getValue()}
                </span>
              )}
              {!w.order_id ? (
                <span className="text-xs text-neutral-600">
                  {t('withdrawals.order_not_matched')}
                </span>
              ) : ends ? (
                <span
                  className={
                    late ? 'text-xs text-amber-800' : 'text-xs text-neutral-600'
                  }
                >
                  {t(
                    late
                      ? 'withdrawals.after_period'
                      : 'withdrawals.period_ends',
                    { date: date(ends) }
                  )}
                </span>
              ) : null}
            </div>
          )
        },
        meta: NOWRAP
      }),
      columnHelper.accessor('status', {
        header: t('withdrawals.status'),
        cell: (info) => {
          const status = STATUSES[info.getValue() ?? '']
          return status ? (
            <Badge tone={status.tone}>{t(status.key)}</Badge>
          ) : null
        },
        meta: NOWRAP
      }),
      columnHelper.accessor('refund_by', {
        header: t('withdrawals.refund_by'),
        cell: (info) => {
          const refundBy = info.getValue()
          if (!refundBy) return null
          const overdue =
            !DONE.includes(info.row.original.status ?? '') &&
            new Date(refundBy) < new Date()
          return (
            <span
              className={
                overdue ? 'text-sm text-red-700' : 'text-sm text-neutral-900'
              }
            >
              {date(refundBy)}
            </span>
          )
        },
        meta: NOWRAP
      }),
      columnHelper.accessor('lang', {
        header: t('withdrawals.language'),
        cell: (info) => {
          const lang = info.getValue()
          if (!lang) return null
          try {
            return languages.of(lang) ?? lang
          } catch {
            // The public form only caps its length, so a malformed tag can get here.
            return lang
          }
        },
        meta: NOWRAP
      }),
      columnHelper.accessor('process_id', {
        header: t('withdrawals.return'),
        cell: (info) => {
          const processId = info.getValue()
          // The row opens it, as on Orders.
          return processId ? (
            <span className="text-primary-base">
              {t('withdrawals.view_return')}
            </span>
          ) : (
            <span className="text-neutral-500">
              {t('withdrawals.no_return')}
            </span>
          )
        },
        meta: NOWRAP
      }),
      columnHelper.display({
        id: 'close',
        header: '',
        cell: (info) => {
          const w = info.row.original
          const closed = w.status === Status.Closed
          // A request with a return follows the return's status; only the rest are marked by hand.
          if (!canClose || (w.process_id && !closed)) return null
          return (
            <Base.Button
              variant="secondary"
              size="sm"
              loading={pendingId === w.id}
              onClick={(event) => {
                // The row opens the return.
                event.stopPropagation()
                toggle(w)
              }}
            >
              {t(closed ? 'withdrawals.reopen' : 'withdrawals.close')}
            </Base.Button>
          )
        },
        meta: NOWRAP
      })
    ]
  }, [t, i18n.language, canClose, pendingId, toggle, ecommerceId, shopifyStore])
}
